// All domain types in one place. Plain data only — no classes, no methods.
import type { Amount } from './logic/quantity';
import type { CustomUnit, UnitRef } from './logic/units';

export type Id = string;
export type Day = string; // 'YYYY-MM-DD' in the user's local calendar
export type MediaRef = { mimeType?: string; uri: string; width?: number; height?: number };

// Same cases and order as Swift's Diet enum.
export const DIETS = [
  'vegan', 'vegetarian', 'glutenFree', 'dairyFree', 'paleo', 'keto', 'mediterranean', 'sattvic', 'rajasic',
  'halal', 'kosher', 'pescatarian', 'ovoVegetarian', 'mormon', 'buddhist', 'hindu', 'shinto', 'taoist',
  'lenten', 'christian', 'jehovah',
] as const;
export type Diet = (typeof DIETS)[number];

export const INGREDIENT_AISLES = [
  'produce', 'bakery', 'dairy', 'meat', 'seafood', 'frozen', 'pantry', 'spices', 'beverages', 'household', 'other',
] as const;
export type IngredientAisle = (typeof INGREDIENT_AISLES)[number];

export const UTENSIL_AISLES = ['cookware', 'bakeware', 'knives', 'tools', 'appliances', 'cleaning', 'other'] as const;
export type UtensilAisle = (typeof UTENSIL_AISLES)[number];

export type IngredientLine = { ingredientId: Id; amount: Amount; unit: UnitRef };
export type UtensilLine = { utensilId: Id; amount: number; scaleEvery: number | null };

export type Step = {
  id: Id;
  title: string;
  explainer: string;
  image?: MediaRef;
  ingredients: IngredientLine[];
  utensils: UtensilLine[];
  timers: { label: string; seconds: number }[];
};

export type Recipe = {
  id: Id;
  title: string;
  explainer: string;
  image?: MediaRef;
  /** BASE servings the amounts are written for. */
  servings: number;
  durationMin: number;
  priceCents: number;
  ingredients: IngredientLine[];
  utensils: UtensilLine[];
  steps: Step[];
  /** Diets the dish breaks beyond its ingredients, e.g. kosher when it combines meat and milk. */
  notDiet?: Diet[];
  canonical?: {id:string;version:string};
  derivedFrom?: {recipeId:string;version:string};
  /** A copy saved from a cooking buddy's post: who shared it, and when. */
  source?: { author: string; sharedAt: number };
  updatedAt: number;
};

/** Nutrient values: kcal for `calories`, milligrams for everything else (as in Swift). */
export type Nutrition = {
  basis: 'per100Grams' | 'perItem';
  values: Partial<Record<NutrientKey, number>>;
};

export type NutrientKey =
  | 'calories' | 'protein' | 'fat' | 'carbohydrates' | 'sugar' | 'fiber'
  | 'iron' | 'zinc' | 'calcium' | 'magnesium' | 'phosphorus' | 'potassium' | 'sodium' | 'vitaminC'
  | (string & {}); // the ~80 micronutrients are listed in nutrition.ts

export type Ingredient = {
  id: Id;
  title: string;
  aisle: IngredientAisle;
  /** Diets this ingredient breaks. Every ingredient must declare them (see dietsChecked). */
  notDiet: Diet[];
  /** User-created ingredients: true once the user confirmed `notDiet` (built-ins always are). */
  dietsChecked?: boolean;
  synonyms: string[];
  similarGroup?: Id;
  explainer: string;
  image?: MediaRef;
  nutrition?: Nutrition;
  builtIn: boolean;
};

export type Utensil = {
  id: Id;
  title: string;
  aisle: UtensilAisle;
  synonyms: string[];
  explainer: string;
  image?: MediaRef;
  builtIn: boolean;
};

export type { CustomUnit };

export type Cookbook = {
  id: Id;
  title: string;
  explainer: string;
  image?: MediaRef;
  sections: { id: Id; title: string; note: string; recipeIds: Id[] }[];
  updatedAt: number;
};

export type Slot = 'breakfast' | 'lunch' | 'dinner';

export type Mealplan = {
  id: Id;
  title: string;
  explainer: string;
  startWeekday: Weekday;
  cycleDays: number;
  entries: { day: number; slot: Slot; recipeId: Id }[];
  updatedAt: number;
};

export type Weekday = 'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday';

export type PlanEntry = { id: Id; date: Day; slot: Slot; recipeId: Id };

export type ShoppingSchedule =
  | { kind: 'everyNDays'; n: number }
  | { kind: 'weekday'; weekday: Weekday; everyNWeeks: number };

export type Planner = { entries: PlanEntry[]; schedule: ShoppingSchedule | null; tripDates: Day[]; updatedAt: number };

export type GroceryItem = {
  id: Id;
  kind: 'ingredient' | 'utensil';
  refId: Id;
  amount: Amount;
  unit: UnitRef;
  checked: boolean;
};

export type GroceryRecipe = { id: Id; recipeId: Id; recipeVersion?:string; servings: number; items: GroceryItem[]; initialCount: number };

export type Grocery = { recipes: GroceryRecipe[]; looseItems: GroceryItem[]; updatedAt: number };

export type Estimate = 'observed' | 'likely' | 'estimated' | 'unknown';

export type PantryItem = {
  kind: 'ingredient' | 'utensil';
  refId: Id;
  amount: number;
  /** Ingredients keep the unit they were bought in (Δ: Swift dropped units). */
  unit: UnitRef;
  expiresOn?: Day;
  dirty?: boolean;
  estimate: Estimate;
};

export type Pantry = { items: PantryItem[]; updatedAt: number };

export type MealLogEntry = { id: Id; day: Day; at: number; photo: MediaRef; recipeId?: Id; caption?: string };

/** The signed-in Chefi account, as the server reports it (/api/me). */
export type AccountPasskey = { id: string; label: string; createdAt: number; lastUsedAt: number | null };
export type AccountUser = { id: Id; displayName: string; passkeys: AccountPasskey[]; telegramLinked: boolean; recoveryCodesRemaining: number;
  /** Navigation hint only; the server authorizes every admin request. */ isAdmin?: boolean;
  /** Active sign-ins (devices). */ sessions: number;
  /** Cooldown for making new recovery codes: when the next set is allowed (ms epoch) and how many are left today. */
  recoveryCreate: { nextAllowedAt: number | null; leftToday: number };
  /** Chefi Super for this account: its own plan (owner) or a seat in someone's (member). */
  super: SuperPlan | null;
};

export type PlanProduct = 'super' | 'family';
/** Super covers 2 people (seats), Family 10. `until` null = no end. */
export type SuperPlan = { plan: PlanProduct; role: 'owner' | 'member'; source: string; until: number | null; seats: number };
export type PlanGift = { id: string; code: string | null; url: string | null; product: PlanProduct; months: number; createdAt: number; redeemed: boolean };
/** GET /api/plan: the plan, its members (owner), its owner (member) and the gifts this account bought. */
export type PlanInfo = { plan: SuperPlan | null; members: { id: string; displayName: string; joinedAt: number }[]; owner: { displayName: string } | null; gifts: PlanGift[] };

export type CheckoutOffer = {
  key: string; product: PlanProduct; kind: 'sub' | 'gift'; interval?: 'month' | 'year'; months?: number;
  amounts: Record<string, number>;
};
export type CheckoutCatalog = { billing?: { currency: string; interval: 'month' | 'year' } | null; currencies: { code: string; divisor: number }[]; offers: CheckoutOffer[] };

/** Shared streaks (server: cloudflare/src/social.ts, sharedStreak.ts). Days are UTC. */
export type SocialPerson = { userId: string; displayName: string };
export type SharedStreak = { count: number; alive: boolean; countsToday: boolean; postedToday: string[]; clearedAt: number };
export type SocialStreak = { id: string; name: string | null; ownerId: string; createdAt: number; members: SocialPerson[]; invited: string[]; streak?: SharedStreak };
export type SocialFriend = SocialPerson & { since: number };
export type StreakInvitation = { circleId: string; name: string | null; invitedBy: string; createdAt: number };
export type SocialOverview = { me: string; maxMembers: number; photos: boolean; publicHandle: string | null; friends: SocialFriend[]; invitations: StreakInvitation[]; streaks: SocialStreak[] };
export type SocialPost = {
  /** This streak's copy; `batchId` is the same in every streak the post went to. */
  id: string; batchId: string; circleId: string;
  author: SocialPerson; mine: boolean; caption: string; recipe: unknown; photo: boolean; createdAt: number; edited: boolean;
  reactions: Record<string, number>; myReaction: string | null;
  /** Kept only on this device: the cloud copy was deleted when the streak ended. */
  archived?: boolean;
};

/** Server AI and assistant connections (server: cloudflare/src/ai.ts, mcp.ts). */
export type AiStatus = { server: boolean; mcpUrl: string; plan: 'free' | 'super'; tasks: string[]; allowance: { perDay: number; used: number; resetAt: number | null }; maxImageBytes: number };
export type AiTaskInput =
  | { task: 'recipe_from_text' | 'ingredients_from_steps'; text: string }
  | { task: 'recipe_from_photo' | 'pantry_from_photo'; image: { mediaType: string; data: string } }
  | { task: 'link_steps'; steps: string[]; ingredients: string[] };
export type AiDraftLine = { name: string; amount: number | null; unit: string | null };
export type AiRecipeDraft = { title: string; servings: number | null; durationMin: number | null; ingredients: AiDraftLine[]; steps: { text: string; ingredients: string[] }[] };
export type AiTaskAnswer = { task: string; recipe?: AiRecipeDraft; items?: AiDraftLine[] };
export type AiToken = { id: string; label: string; scope: 'read' | 'propose' | 'full'; createdAt: number; lastUsedAt: number | null; expiresAt: number; oauth?: boolean | number };
export type AiInboxItem =
  | { id: string; kind: 'recipe'; payload: AiRecipeDraft; note: string; createdAt: number; source: string | null }
  | { id: string; kind: 'grocery' | 'pantry'; payload: { items: AiDraftLine[] }; note: string; createdAt: number; source: string | null }
  | { id: string; kind: 'plan'; payload: { entries: { date: string; slot: Slot; recipeId: string; title: string }[] }; note: string; createdAt: number; source: string | null };
/** Creators: the only public, searchable accounts (approval only; server: cloudflare/src/creators.ts). */
export type CreatorProfile = { handle: string; displayName: string; bio: string; links: string[] };
export type CreatorApplication = CreatorProfile & { status: 'pending' | 'approved' | 'rejected'; note: string | null; createdAt: number; decidedAt: number | null };
export type MyCreator = { application: CreatorApplication | null; profile: CreatorProfile | null };
/** What a creator posted publicly (anyone can see it). */
export type CreatorPost = { mediaType?: string | null; id: string; caption: string; recipe: unknown; photo: boolean; createdAt: number; edited: boolean };

/** Private relay conversations; old social groups are exported separately. */
export type FriendThread = { id: string; userA: string; userB: string; count: number; day: string | null; broken: number; canRestore: boolean; friend: { userId: string; displayName: string } };
export type FriendPost = { id: string; threadId: string; authorId: string; authorName: string; caption: string; recipe: unknown; photo: boolean; mediaType: string | null; createdAt: number; updatedAt: number; replyTo: string | null; replyDueDay: string | null; repliedAt: number | null; deletedAt: number | null; localMedia?: MediaRef };
export type FriendsOverview = { me: string; started: boolean; cloudArchive: boolean; threads: FriendThread[]; legacyCount: number; legacyUntil?: number | null; relayDays: number; publicHandle?: string | null; archiveBytes: number; archiveLimit: number | null };
